import type {
  HorizontalPosition,
  MovementResolution,
  MovementResolutionRequest,
} from "../core/types";
import type {
  CollisionShape,
  CollisionWorldOptions,
  MovementStepConstraint,
  WalkSurfaceQuery,
} from "./types";

const EPSILON = 1e-9;

interface LocalBox {
  centerX: number;
  centerZ: number;
  halfWidth: number;
  halfDepth: number;
  cos: number;
  sin: number;
}

export class CollisionWorld {
  private readonly shapes = new Map<string, CollisionShape>();
  private readonly maxIterations: number;
  private readonly maxSubstep: number;
  private readonly surface?: WalkSurfaceQuery;
  private readonly canOccupy?: MovementStepConstraint;

  constructor(options: CollisionWorldOptions = {}) {
    this.maxIterations = Math.max(1, Math.floor(options.maxIterations ?? 6));
    this.maxSubstep = Math.max(0.02, options.maxSubstep ?? 0.12);
    this.surface = options.surface;
    this.canOccupy = options.canOccupy;
  }

  add(shape: CollisionShape): void {
    validateShape(shape);
    this.shapes.set(shape.id, { ...shape });
  }

  remove(id: string): boolean {
    return this.shapes.delete(id);
  }

  clear(): void {
    this.shapes.clear();
  }

  setEnabled(id: string, enabled: boolean): boolean {
    const shape = this.shapes.get(id);
    if (!shape) return false;
    shape.enabled = enabled;
    return true;
  }

  resolveMovement(request: MovementResolutionRequest): MovementResolution {
    validateRequest(request);
    const startX = request.position.x;
    const startZ = request.position.z;
    const distance = Math.hypot(request.displacement.x, request.displacement.z);
    if (distance <= EPSILON) {
      return {
        position: { x: startX, z: startZ },
        displacement: { x: 0, z: 0 },
        collided: false,
      };
    }

    const stepLimit = Math.min(this.maxSubstep, Math.max(request.radius * 0.5, 0.02));
    const stepCount = Math.max(1, Math.ceil(distance / stepLimit));
    const stepX = request.displacement.x / stepCount;
    const stepZ = request.displacement.z / stepCount;
    const position = { x: startX, z: startZ };
    let collided = false;

    for (let step = 0; step < stepCount; step += 1) {
      const from = { x: position.x, z: position.z };
      const candidate = this.resolveSolidStep(from, stepX, stepZ, request.radius);
      collided ||= candidate.collided;

      if (this.isStepAllowed(from, candidate.position, request)) {
        position.x = candidate.position.x;
        position.z = candidate.position.z;
        continue;
      }

      collided = true;
      const xOnly = this.resolveSolidStep(from, stepX, 0, request.radius);
      if (this.isStepAllowed(from, xOnly.position, request)) {
        position.x = xOnly.position.x;
        position.z = xOnly.position.z;
        continue;
      }

      const zOnly = this.resolveSolidStep(from, 0, stepZ, request.radius);
      if (this.isStepAllowed(from, zOnly.position, request)) {
        position.x = zOnly.position.x;
        position.z = zOnly.position.z;
      }
    }

    return {
      position,
      displacement: { x: position.x - startX, z: position.z - startZ },
      collided,
    };
  }

  private resolveSolidStep(
    from: Readonly<HorizontalPosition>,
    movementX: number,
    movementZ: number,
    radius: number,
  ): { position: HorizontalPosition; collided: boolean } {
    const position = { x: from.x + movementX, z: from.z + movementZ };
    let collided = false;

    for (let iteration = 0; iteration < this.maxIterations; iteration += 1) {
      let corrected = false;
      for (const shape of this.shapes.values()) {
        if (shape.enabled === false) continue;
        const correction = circleBoxCorrection(position, radius, shape, movementX, movementZ);
        if (!correction) continue;
        position.x += correction.x;
        position.z += correction.z;
        corrected = true;
        collided = true;
      }
      if (!corrected) break;
    }

    return { position, collided };
  }

  private isStepAllowed(
    from: Readonly<HorizontalPosition>,
    to: Readonly<HorizontalPosition>,
    request: Readonly<MovementResolutionRequest>,
  ): boolean {
    if (this.surface && !isTerrainStepAllowed(this.surface, from, to, request)) return false;
    return this.canOccupy?.(from, to, request) ?? true;
  }
}

function circleBoxCorrection(
  position: Readonly<HorizontalPosition>,
  radius: number,
  shape: CollisionShape,
  movementX: number,
  movementZ: number,
): HorizontalPosition | null {
  const box = toLocalBox(shape);
  const dx = position.x - box.centerX;
  const dz = position.z - box.centerZ;
  const localX = dx * box.cos + dz * box.sin;
  const localZ = -dx * box.sin + dz * box.cos;
  const closestX = clamp(localX, -box.halfWidth, box.halfWidth);
  const closestZ = clamp(localZ, -box.halfDepth, box.halfDepth);
  const separationX = localX - closestX;
  const separationZ = localZ - closestZ;
  const separationSquared = separationX * separationX + separationZ * separationZ;
  if (separationSquared >= radius * radius) return null;

  let correctionX: number;
  let correctionZ: number;
  if (separationSquared > EPSILON) {
    const separation = Math.sqrt(separationSquared);
    const correction = radius - separation;
    correctionX = separationX / separation * correction;
    correctionZ = separationZ / separation * correction;
  } else {
    const distanceX = box.halfWidth - Math.abs(localX);
    const distanceZ = box.halfDepth - Math.abs(localZ);
    const localMovementX = movementX * box.cos + movementZ * box.sin;
    const localMovementZ = -movementX * box.sin + movementZ * box.cos;
    if (distanceX <= distanceZ) {
      correctionX = outwardSign(localX, -localMovementX) * (distanceX + radius);
      correctionZ = 0;
    } else {
      correctionX = 0;
      correctionZ = outwardSign(localZ, -localMovementZ) * (distanceZ + radius);
    }
  }

  return {
    x: correctionX * box.cos - correctionZ * box.sin,
    z: correctionX * box.sin + correctionZ * box.cos,
  };
}

function toLocalBox(shape: CollisionShape): LocalBox {
  if (shape.shape === "obb") {
    return {
      centerX: shape.centerX,
      centerZ: shape.centerZ,
      halfWidth: shape.halfWidth,
      halfDepth: shape.halfDepth,
      cos: Math.cos(shape.rotation),
      sin: Math.sin(shape.rotation),
    };
  }

  return {
    centerX: (shape.minX + shape.maxX) * 0.5,
    centerZ: (shape.minZ + shape.maxZ) * 0.5,
    halfWidth: (shape.maxX - shape.minX) * 0.5,
    halfDepth: (shape.maxZ - shape.minZ) * 0.5,
    cos: 1,
    sin: 0,
  };
}

function isTerrainStepAllowed(
  surface: WalkSurfaceQuery,
  from: Readonly<HorizontalPosition>,
  to: Readonly<HorizontalPosition>,
  request: Readonly<MovementResolutionRequest>,
): boolean {
  const currentGround = surface.getHeight(from.x, from.z);
  const nextGround = surface.getHeight(to.x, to.z);
  const currentWaterDepth = Math.max(0, surface.getWaterDepth(from.x, from.z));
  const nextWaterDepth = Math.max(0, surface.getWaterDepth(to.x, to.z));
  const leavingWater = currentWaterDepth > 0 && nextWaterDepth < currentWaterDepth;
  return (
    (nextGround - currentGround <= request.maxStepHeight || leavingWater) &&
    nextWaterDepth <= request.maxWadeDepth
  );
}

function validateShape(shape: CollisionShape): void {
  if (!shape.id) throw new Error("Collision shapes require a non-empty id");
  const values = shape.shape === "aabb"
    ? [shape.minX, shape.maxX, shape.minZ, shape.maxZ]
    : [shape.centerX, shape.centerZ, shape.halfWidth, shape.halfDepth, shape.rotation];
  if (!values.every(Number.isFinite)) throw new Error(`Collision shape ${shape.id} has non-finite values`);
  if (shape.shape === "aabb" && (shape.maxX <= shape.minX || shape.maxZ <= shape.minZ)) {
    throw new Error(`Collision AABB ${shape.id} must have positive dimensions`);
  }
  if (shape.shape === "obb" && (shape.halfWidth <= 0 || shape.halfDepth <= 0)) {
    throw new Error(`Collision OBB ${shape.id} must have positive dimensions`);
  }
}

function validateRequest(request: MovementResolutionRequest): void {
  const values = [
    request.position.x,
    request.position.z,
    request.displacement.x,
    request.displacement.z,
    request.radius,
    request.maxStepHeight,
    request.maxWadeDepth,
  ];
  if (!values.every(Number.isFinite)) throw new Error("Movement request contains non-finite values");
  if (request.radius <= 0) throw new Error("Movement radius must be positive");
}

function outwardSign(primary: number, fallback: number): number {
  const value = Math.abs(primary) > EPSILON ? primary : fallback;
  return value < 0 ? -1 : 1;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
